import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Chapter from "./Chapter";
import { FRAMEWORKS, CANONICAL_TOPICS_68 } from "@/lib/data";

/**
 * ONE pinned scroll-scrub — "The Canonical Layer".
 * 20 framework stacks slide inward and collapse into ~68 shared topic tiles beneath them.
 * No scroll-jack: uses standard sticky + scroll-progress transforms; exits normally.
 */
export default function CanonicalLayer() {
  const wrap = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrap,
    offset: ["start start", "end end"],
  });

  const collapse = useTransform(scrollYProgress, [0, 0.4, 0.75, 1], [0, 0.65, 1, 1]);
  const topicOpacity = useTransform(scrollYProgress, [0.35, 0.75], [0, 1]);
  const stackOpacity = useTransform(scrollYProgress, [0.55, 0.9], [1, 0.15]);

  const progressWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section ref={wrap} id="canonical" className="relative" style={{ height: "160vh" }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-25 pointer-events-none" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 py-12 h-full flex flex-col">
          <Chapter number="03" label="The canonical layer" id="canonical-chapter" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-1">
            <div className="lg:col-span-4">
              <h2 className="font-display text-[36px] md:text-[52px] leading-[1.04] tracking-[-0.022em] text-balance">
                Twenty stacks. <br/><span className="grad-text">One layer.</span>
              </h2>
              <p className="mt-5 text-ink-muted text-[15px] leading-relaxed max-w-[42ch]">
                Scroll to watch the frameworks fold into their shared topics. The atomic unit of compliance work is the topic — not the framework.
              </p>

              <div className="mt-8 w-full max-w-[280px]">
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                  <span>Collapse</span>
                  <span>20 → 68</span>
                </div>
                <div className="mt-2 h-[3px] w-full bg-white/8 relative overflow-hidden rounded-full">
                  <motion.div className="absolute inset-y-0 left-0 grad-bg rounded-full" style={{ width: progressWidth }} />
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 relative">
              {/* framework stacks */}
              <motion.div
                style={{ opacity: stackOpacity }}
                className="absolute inset-0 grid grid-cols-4 md:grid-cols-5 gap-3 content-center"
              >
                {FRAMEWORKS.map((f, i) => (
                  <FrameworkStack key={f.id} f={f} i={i} collapse={collapse} />
                ))}
              </motion.div>

              {/* topic tiles emerging — 68 real canonical topics */}
              <motion.div
                style={{ opacity: topicOpacity }}
                className="absolute inset-0 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5 content-center"
              >
                {CANONICAL_TOPICS_68.map((label, i) => (
                  <motion.div
                    key={label + i}
                    initial={false}
                    className="aspect-[3/2] rounded-md border flex items-center justify-center px-1.5 text-center"
                    style={{ borderColor: "var(--hairline)", background: "color-mix(in oklab, var(--text) 3%, transparent)" }}
                    animate={{ borderColor: ["var(--hairline)", "var(--hairline-strong)", "var(--hairline)"] }}
                    transition={{ duration: 4, delay: (i % 14) * 0.05, repeat: Infinity, ease: "easeInOut" }}
                    data-testid={`canonical-topic-tile-${i}`}
                  >
                    <span
                      className="font-mono leading-tight text-center"
                      style={{ fontSize: "9px", letterSpacing: "0.01em", color: "color-mix(in oklab, var(--text) 72%, transparent)" }}
                    >
                      {label}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FrameworkStack({ f, i, collapse }) {
  const dxRange = ((i % 5) - 2) * -60;
  const dyRange = (Math.floor(i / 5) - 1.5) * -40;
  const x = useTransform(collapse, [0, 1], [0, dxRange]);
  const y = useTransform(collapse, [0, 1], [0, dyRange]);
  const scale = useTransform(collapse, [0, 1], [1, 0.6]);
  const opacity = useTransform(collapse, [0, 0.85, 1], [1, 0.5, 0]);
  return (
    <motion.div
      style={{ x, y, scale, opacity }}
      className="machined px-3 py-3 flex flex-col items-start gap-1 min-h-[68px]"
    >
      <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-white/45">{f.family}</span>
      <span className="font-display text-[13px] leading-tight">{f.name}</span>
      <span className="font-mono text-[9px] text-white/40">{f.code}</span>
    </motion.div>
  );
}
